import { useState } from 'react'
import { Container, Paper, Title, TextInput, PasswordInput, Button, Text, Anchor, Stack, Select, Alert } from '@mantine/core'
import { useForm } from '@mantine/form'
import { notifications } from '@mantine/notifications'
import { useNavigate } from 'react-router-dom'
import { Info } from 'lucide-react'
import { useAuth } from '../../context/AuthContext'

export function RegisterPage() {
  const { register } = useAuth()
  const navigate = useNavigate()
  const [verificationSent, setVerificationSent] = useState(false)

  const form = useForm({
    initialValues: { email: '', password: '', user_name: '', role: 'student' },
    validate: {
      email: (v) => (/^\S+@\S+$/.test(v) ? null : 'Invalid email'),
      password: (v) => (v.length >= 6 ? null : 'Password must be at least 6 characters'),
      user_name: (v) => (v.length >= 2 ? null : 'Name is required'),
    },
  })

  const handleSubmit = async (values: typeof form.values) => {
    try {
      await register(values)
      setVerificationSent(true)
    } catch (err: any) {
      notifications.show({
        title: 'Registration failed',
        message: err.response?.data?.detail || 'Something went wrong',
        color: 'red',
      })
    }
  }

  return (
    <Container size={420} my={80}>
      <Title ta="center" style={{ color: 'var(--sat-text)' }}>Create Account</Title>
      <Text c="dimmed" size="sm" ta="center" mt={5}>
        Already have an account?{' '}
        <Anchor size="sm" onClick={() => navigate('/login')}>Sign in</Anchor>
      </Text>

      <Paper withBorder shadow="md" p={30} mt={30} radius="md" bg="var(--sat-bg)">
        {verificationSent ? (
          <Stack>
            <Alert icon={<Info size={16} />} color="teal" radius="md">
              <Text fw={500} mb={4}>Check your inbox!</Text>
              <Text size="sm">
                A verification link has been sent to your email address. Please verify your email before signing in.
                If you don't see it, check your spam folder.
              </Text>
            </Alert>
            <Button fullWidth variant="outline" color="grapefruit" onClick={() => navigate('/login')}>
              Go to Login
            </Button>
          </Stack>
        ) : (
          <form onSubmit={form.onSubmit(handleSubmit)}>
            <Stack>
              <TextInput label="Name" placeholder="Your name" required {...form.getInputProps('user_name')} />
              <TextInput label="Email" placeholder="you@example.com" required {...form.getInputProps('email')} />
              <PasswordInput label="Password" placeholder="At least 6 characters" required {...form.getInputProps('password')} />
              <Select
                label="I am a..."
                data={[
                  { value: 'student', label: 'Student' },
                  { value: 'tutor', label: 'Tutor' },
                ]}
                {...form.getInputProps('role')}
              />
              <Button fullWidth mt="xl" type="submit" color="grapefruit">Register</Button>
            </Stack>
          </form>
        )}
      </Paper>
    </Container>
  )
}
